/**
 * FlowsList — Iter Feb 2026 R43 · Phase 5b.
 *
 * Lists all diagrams published as Flows (`kind=flow`) and their runs.
 * The visual editor is the existing R39 Diagram canvas — this page is
 * the "runbook" surface: what's live, what's firing, what's failing.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Zap, ExternalLink, CheckCircle2, XCircle, RefreshCw, Play, PauseCircle,
} from "lucide-react";

export default function FlowsList() {
  const { activeWorkspaceId } = useAuth();
  const [flows, setFlows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [actionCatalog, setActionCatalog] = useState({});
  const [eventCatalog, setEventCatalog] = useState({});

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const [{ data: a }, { data: b }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/flows`),
        api.get(`/workspaces/${activeWorkspaceId}/flow-runs?limit=30`),
      ]);
      setFlows(a.rows || []);
      setActionCatalog(a.action_catalog || {});
      setEventCatalog(a.event_catalog || {});
      setRuns(b.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col h-full" data-testid="flows-list-page">
      <PageHeader
        eyebrow="Automation"
        title="Acquilix Flows"
        description="Diagrams that RUN. Publish any R39 diagram as a flow bound to an event trigger — it executes every action node when the event fires. No YAML, no code."
        actions={
          <Button variant="outline" className="rounded-none h-9" onClick={load} data-testid="flows-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        }
      />
      <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-10 py-4 space-y-4">

        {/* Empty state */}
        {flows.length === 0 && (
          <div className="border-2 border-dashed p-10 text-center space-y-3">
            <Zap className="w-10 h-10 text-neutral-400 mx-auto" />
            <div className="font-semibold">No flows published yet</div>
            <div className="text-sm text-muted-foreground max-w-md mx-auto">
              Design a workflow in <b>Diagrams</b>, add a trigger node
              (event) + action nodes (webhook.post, task.create, tag.add,
              etc.), then click <b>Publish as Flow</b> in the editor.
            </div>
            <Link to="/app/diagrams"><Button className="rounded-none">
              <ExternalLink className="w-4 h-4 mr-1" /> Open Diagrams
            </Button></Link>
          </div>
        )}

        {/* Published flows */}
        {flows.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {flows.map(f => (
              <div key={f.id} className="border p-3" data-testid={`flow-card-${f.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${f.flow_enabled ? "bg-emerald-500" : "bg-neutral-400"}`} />
                  <div className="font-semibold text-sm flex-1 truncate">{f.title}</div>
                  <Link to={`/app/diagrams/${f.id}`}>
                    <Button size="sm" variant="outline" className="rounded-none h-7">Edit</Button>
                  </Link>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Trigger: <code className="font-mono border px-1">{f.flow_trigger_event}</code></div>
                  <div>Published: {f.flow_published_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Recent runs */}
        <div className="border">
          <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">
            Recent runs · {runs.length}
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr className="border-b">
                <th className="text-left px-2 py-1.5">Flow</th>
                <th className="text-left px-2 py-1.5">Event</th>
                <th className="text-left px-2 py-1.5">Started</th>
                <th className="text-right px-2 py-1.5">Steps</th>
                <th className="text-left px-2 py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">No runs yet.</td></tr>
              )}
              {runs.map(r => {
                const ok = r.status === "succeeded";
                const Icon = ok ? CheckCircle2 : XCircle;
                const color = ok ? "text-emerald-600" : r.status === "partial" ? "text-amber-600" : "text-rose-600";
                return (
                  <tr key={r.id} className="border-b hover:bg-neutral-50" data-testid={`flow-run-${r.id}`}>
                    <td className="px-2 py-1 truncate max-w-[220px]">
                      <Link to={`/app/diagrams/${r.flow_id}`} className="hover:underline">{r.flow_title}</Link>
                    </td>
                    <td className="px-2 py-1 font-mono text-[10px]">{r.event}</td>
                    <td className="px-2 py-1 text-[10px]">{r.started_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="px-2 py-1 text-right font-mono">{r.step_count} <span className="text-rose-500">{r.error_count > 0 ? `·${r.error_count} err` : ""}</span></td>
                    <td className="px-2 py-1">
                      <span className={`inline-flex items-center gap-1 ${color}`}>
                        <Icon className="w-3 h-3" /> {r.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Actions & events reference */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border">
            <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">Available actions</div>
            {Object.entries(actionCatalog).map(([k, v]) => (
              <div key={k} className="border-b px-3 py-1.5 text-xs">
                <code className="font-mono">{k}</code>
                <div className="text-muted-foreground text-[11px]">{v}</div>
              </div>
            ))}
          </div>
          <div className="border">
            <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">Available events (triggers)</div>
            {Object.entries(eventCatalog).map(([k, v]) => (
              <div key={k} className="border-b px-3 py-1.5 text-xs">
                <code className="font-mono">{k}</code>
                <div className="text-muted-foreground text-[11px]">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
